import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { themes, ThemeType } from '../styles/theme';

interface ThemeContextType {
  currentTheme: ThemeType;
  setTheme: (theme: ThemeType) => void;
  theme: any;
  availableThemes: { key: ThemeType; name: string; description: string; preview: string }[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<ThemeType>(() => {
    // Load theme from localStorage or default to 'dark'
    const savedTheme = localStorage.getItem('smartbi-theme') as ThemeType;
    return savedTheme && savedTheme in themes ? savedTheme : 'light';
  });

  const setTheme = (theme: ThemeType) => {
    // Add transition class before theme change
    document.documentElement.classList.add('theme-transition');
    
    setCurrentTheme(theme);
    localStorage.setItem('smartbi-theme', theme);
    
    // Remove transition class after animation completes
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transition');
    }, 400);
  };

  const theme = themes[currentTheme];

  // Available themes with descriptions
  const availableThemes = [
    {
      key: 'dark' as ThemeType,
      name: 'Classic Dark',
      description: 'Original dark theme with purple accents',
      preview: 'linear-gradient(135deg, #7E3AF2 0%, #1F2937 100%)'
    },
    {
      key: 'light' as ThemeType,
      name: 'Smart Light',
      description: 'Light theme with vibrant lime green accents',
      preview: 'linear-gradient(135deg, #FFFFFF 0%, #F7F9FA 50%, #C0F731 100%)'
    }
  ];

  // Apply theme to document root using CSS classes
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove existing theme classes
    root.classList.remove('theme-dark', 'theme-cyber', 'theme-light');
    
    // Add current theme class - CSS variables are handled by themes.css
    root.classList.add(`theme-${currentTheme}`);
    
    // Also add data attribute for easier targeting
    root.setAttribute('data-theme', currentTheme);
  }, [currentTheme]);

  const value = {
    currentTheme,
    setTheme,
    theme,
    availableThemes,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};